import { keyframes } from "@emotion/react";
import { Stack } from "@mui/material";
import { styled } from "@mui/material/styles";

import { dontForwardProps } from "@/lib/styled";
import Button from "@/components/button/button";
import Select from "@/components/select/select";

export const MapStack = styled(Stack)`
  container-name: map;
  container-type: size;
  flex-shrink: 0;
  position: relative;
`;

export const ControlPosition = styled("div")`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 2px;
  left: 10px;
  top: 10px;
  position: absolute;
`;

export const ControlButtonGroup = styled("div")({
  display: "flex",
  flexDirection: "row",
  gap: 2,
});

const controlRadius = 6;

/* Map control panels and chrome: ~60% white + blur (aligned with main / PRD) */
const mapControlSurfaceWhite = "rgba(255, 255, 255, 0.8)";

const controlPanelFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const ControlToggle = styled(Button, {
  shouldForwardProp: dontForwardProps("open", "active"),
})<{ open?: boolean; active?: boolean }>(({ theme, active }) => ({
  /* TODO: frosted class unification */
  background: active ? "rgba(255, 255, 255, 0.85)" : mapControlSurfaceWhite,
  backdropFilter: "blur(2px)",
  border: active
    ? `1px solid ${theme.palette.primary.main}`
    : "1px solid rgba(255, 255, 255, 0)",
  borderRadius: controlRadius,
  color: "var(--black)",
  margin: 0,
  padding: "var(--spacing-2x)",
}));

export const ControlElement = styled("div")({
  animation: `${controlPanelFadeIn} 0.2s ease`,
  borderRadius: controlRadius,
  marginTop: "var(--spacing)",
  minWidth: 200,
  overflow: "hidden",
  position: "relative",
  zIndex: 4000000,
});

export const ConfigPanelStack = styled("div")({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-2x)",
});

export const LayersPanelContainer = styled("div")({
  borderRadius: controlRadius,
  minWidth: 200,
  overflow: "hidden",
});

export const EnumPanelContainer = styled("div")({
  borderRadius: controlRadius,
  minWidth: 200,
  overflow: "hidden",
});

/** Legend header row: padding/divider aligned with map panels; normal weight (matches ghost Select) */
export const LegendPanelHeader = styled("div")(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontSize: 13,
  fontWeight: 400,
  padding: theme.spacing(1, 1.5),
}));

export const ControlItem = styled("div")<{
  isActive: boolean;
  isForecast: boolean;
  isReady: boolean;
  isEnabled: boolean;
}>(({ theme, isActive, isForecast, isEnabled, isReady }) => ({
  /* Panel (LayersPanelContainer) owns frosted background; rows are content only */
  background: isEnabled && isForecast ? "#D52B1EE6" : "transparent",
  color: isEnabled && isForecast ? "white" : theme.palette.text.primary,
  fontSize: "13px",
  padding: theme.spacing(1, 1.5, 1, 1.5),
  opacity: isEnabled && isReady ? (isActive ? 1 : 0.7) : 1,

  "&:not(:first-of-type)": {
    marginTop: 0,
  },
}));

export const LayerToggleRow = styled("div")({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: 4,
  width: "100%",
});

export const LayerToggleLabel = styled("div")({
  flex: "1 0 auto",
  minWidth: 0,
});

export const LayerToggleStateCell = styled("div")({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  justifyContent: "center",
  minWidth: 32,
});

export const LegendIconButton = styled("span")({
  cursor: "pointer",
  display: "inline-flex",
  flexShrink: 0,
  marginRight: 2,
});

export const LayerToggleDropdownsContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
});

export const LegendItem = styled("div")(
  ({ theme }) => `
  color: ${theme.palette.text.primary};
  fontSize: 13px;
  width: 100%;
`,
);

/** Error state inside the legend tooltip: message stacked above a retry action. */
export const LegendError = styled("div")(
  ({ theme }) => `
  align-items: flex-start;
  color: ${theme.palette.text.primary};
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: var(--spacing);
`,
);

export const ControlLegend = styled("div")({
  background: "color-mix(in srgb, white 60%, transparent)",
  borderRadius: "0 4px 4px 4px",
  padding: "var(--spacing)",
});

export const MapMarkerWrapper = styled("div")(
  () => `
  align-items: center;
  display: flex;
  font-weight: bold;
  justify-content: center;
  position: relative;
  text-align: center;
  transition: 0.15s ease background;
`,
);

export const MapMarker = styled("div")<{
  multiple?: boolean;
  state?: "active" | "hover";
}>(
  ({ multiple = false, state = undefined }) => `
  align-items: center;
  align-content: center;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 4px;
  bottom: 10px;
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.5);
  color: black;
  cursor: pointer;
  display: flex;
  height: 100%;
  position: relative;

  &::after {
    border: 5px solid transparent;
    border-top-color: rgba(255, 255, 255, ${multiple || state ? "1" : "0.75"});
    bottom: -10px;
    content: '';
    height: 0;
    left: calc(50% - 5px);
    position: absolute;
    width: 0;
  }

  ${
    state === "hover"
      ? `
    background: rgba(255, 255, 255, 1);
    `
      : ""
  }

  ${
    state === "active"
      ? `
    background: rgba(0, 0, 0, 1);
    color: white;

    &::after {
      border-top-color: rgba(0, 0, 0, 1);
    }
    `
      : ""
  }
`,
);

export const MapMarkerValue = styled("span", {
  shouldForwardProp: dontForwardProps("inverted", "state", "thresholdColor"),
})<{
  inverted?: boolean;
  state?: "active" | "hover";
  /** Resolved CSS color applied to just this cell's label when a threshold is active. */
  thresholdColor?: string;
}>(
  ({ inverted = false, state = undefined, thresholdColor = undefined }) => `
    align-items: center;
    display: flex;
    height: 100%;
    gap: 4px;
    min-height: 29px;
    padding: 0 6px;
    white-space: nowrap;
    width: calc(100% + 12px);

    ${
      inverted && state === undefined
        ? `
        background: rgba(255, 255, 255, 1);
        border-radius: 4px 0 0 4px;
        `
        : ""
    }

    ${
      /* Active (selected) marker keeps its inverted white-on-black styling. */
      thresholdColor && state !== "active"
        ? `
        color: ${thresholdColor};
        text-shadow: 0 0 2px rgba(255, 255, 255, 0.9);
        `
        : ""
    }
  `,
);

export const MapMarkerValueColor = styled("span")`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  height: 16px;
  width: 16px;
`;

export const LegendListItem = styled("div")<{
  markerColor: string;
}>(
  ({ markerColor }) => `
  align-items: center;
  color: black;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  gap: 8px;

  &::before {
    background: ${markerColor};
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    content: '';
    display: inline-block;
    height: 14px;
    width: 14px;
  }
`,
);

export const LegendListTitle = styled("div")`
  color: black;
  font-weight: bold;
  margin-block: 4px;
`;

/**
 * Per-layer enum setting dropdown. Wraps the shared Ark Select so its popover
 * portals above (z-index) and joins the same dismissable-layer stack as a dialog
 * that can host the map (CMS widget preview) — a MUI Select menu would render
 * behind such a dialog and trip its outside-click dismissal.
 *
 * The shared wrapper defaults to `min-width: 180px`; the layer panel is narrow,
 * so let it shrink to the panel width and cap the width so a long option label
 * ellipsis-truncates rather than growing the control. `&&` doubles the class
 * specificity to win over the shared wrapper rule regardless of injection order.
 */
export const ControlDropdown = styled(Select)({
  "&&": {
    marginBlock: "var(--spacing)",
    maxWidth: 200,
    minWidth: 0,
    width: "100%",
  },
  // Let the trigger shrink below its content so the value can truncate.
  '& [data-part="trigger"]': {
    minWidth: 0,
  },
  '& [data-part="value-text"]': {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

/**
 * Popover list for {@link ControlDropdown}. The content is portaled, so it
 * cannot be reached from the wrapper; this styles the custom content node
 * directly to wrap long option labels instead of scrolling horizontally (the
 * shared content only manages vertical scroll).
 */
export const ControlDropdownContent = styled("div")({
  overflowX: "hidden",
  '& [data-part="item"]': {
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },
});
